/**
 * Hook to track query invalidations and refetch when tab becomes visible
 *
 * When realtime events arrive while the browser tab is hidden, React Query's
 * invalidateQueries() marks queries as stale but may not immediately refetch.
 * This hook tracks which queries were invalidated in the background and
 * refetches them all when the user returns to the tab.
 */

import { useRef, useEffect, useCallback } from 'react';
import { useQueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Serialize a query key to a string for Set storage
 */
function serializeKey(key: QueryKey): string {
  return JSON.stringify(key);
}

/**
 * Hook that tracks query invalidations while tab is hidden
 * and refetches them when visibility returns
 *
 * @example
 * ```tsx
 * const { trackInvalidation } = usePendingRefetch();
 *
 * // When invalidating a query while potentially in background:
 * const queryKey = ['collaborative', 'voting', urlKey];
 * trackInvalidation(queryKey);
 * queryClient.invalidateQueries({ queryKey });
 * ```
 */
export function usePendingRefetch() {
  const queryClient = useQueryClient();
  const pendingKeysRef = useRef<Set<string>>(new Set());

  /**
   * Track a query key for pending refetch if document is hidden
   */
  const trackInvalidation = useCallback((queryKey: QueryKey) => {
    if (document.hidden) {
      pendingKeysRef.current.add(serializeKey(queryKey));
      console.debug('[Realtime] Tracked pending invalidation:', queryKey);
    }
  }, []);

  /**
   * Refetch all pending queries when tab becomes visible
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && pendingKeysRef.current.size > 0) {
        const pendingCount = pendingKeysRef.current.size;
        console.debug(`[Realtime] Tab visible - refetching ${pendingCount} pending queries`);

        pendingKeysRef.current.forEach((serializedKey) => {
          try {
            const queryKey = JSON.parse(serializedKey) as QueryKey;
            queryClient.refetchQueries({ queryKey });
          } catch (error) {
            console.error('[Realtime] Failed to parse pending query key:', serializedKey, error);
          }
        });

        pendingKeysRef.current.clear();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [queryClient]);

  return { trackInvalidation };
}
